/* eslint-disable no-param-reassign */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  V2UploadState,
  V2File,
  V2Operation,
  V2Bit,
  V2Basics,
  V2JobDetails,
  V2GroupDetails,
} from './v2Upload.d';
import { GcodeExtractedData } from '@/workers/gcodeParser';
import { CamExtractedData } from '@/workers/camTypes';
import { EMPTY_JOB_DETAILS, mergeJobDetails } from './utils/mergeSources';
import {
  byCutOrder,
  cutKey,
  pairsOneForOne,
  sortInPlace,
} from './utils/cutOrder';

// ---------------------------------------------------------------------------
// Reducer helpers. Split out of the reducers below purely to keep each case
// simple; they mutate the immer draft they're handed, exactly as inline code
// would (no-param-reassign is disabled file-wide for the same reason).
// ---------------------------------------------------------------------------

/** Insert a file id into `list` before `beforeId`, or at the end if absent. */
function insertBeforeId(
  list: string[],
  id: string,
  beforeId: string | null
): void {
  const at = beforeId ? list.indexOf(beforeId) : -1;
  list.splice(at < 0 ? list.length : at, 0, id);
}

function reAddFile(state: V2UploadState, file: V2File, index: number): void {
  if (state.files.some((f) => f.id === file.id)) return;
  state.files.splice(Math.min(index, state.files.length), 0, file);
}

function reAddToOp(
  state: V2UploadState,
  opId: string | null,
  beforeFileId: string | null,
  fileId: string
): void {
  if (!opId) return;
  const op = state.operations.find((o) => o.id === opId);
  if (!op || op.fileIds.includes(fileId)) return;
  insertBeforeId(op.fileIds, fileId, beforeFileId);
}

function reAddThumbnail(
  state: V2UploadState,
  wasThumbnail: boolean,
  fileId: string
): void {
  if (wasThumbnail && !state.thumbnailFileId) state.thumbnailFileId = fileId;
}

/** Only this file's removal could have cleared the parser output (it was the
 * last G-code); bring it back with the file. */
function reAddParse(
  state: V2UploadState,
  file: V2File,
  extractedData: GcodeExtractedData | null
): void {
  if (file.kind !== 'gcode' || !extractedData || state.extractedData) return;
  state.extractedData = extractedData;
  state.parsedReady = true;
}

/** Only the files that produced `camExtracted` can invalidate it: a removed
 * companion leaves the main file's data, a removed main file leaves the
 * companion's, and an unparsed design file changes nothing. */
function clearCamIfProducerGone(state: V2UploadState, gone: Set<string>): void {
  const cam = state.camExtracted;
  if (!cam) return;
  if (cam.companion && gone.has(cam.companion.fileId)) delete cam.companion;
  if (!gone.has(state.camExtractedFileId ?? '')) return;
  const kept = cam.companion;
  state.camExtracted = kept ? kept.data : null;
  state.camExtractedFileId = kept ? kept.fileId : null;
}

/** After files leave, drop the thumbnail if it went and the parser output if
 * no G-code remains. */
function afterFilesRemoved(state: V2UploadState, gone: Set<string>): void {
  if (state.thumbnailFileId && gone.has(state.thumbnailFileId)) {
    state.thumbnailFileId = null;
  }
  if (!state.files.some((f) => f.kind === 'gcode')) {
    state.extractedData = null;
    state.parsedReady = false;
  }
  clearCamIfProducerGone(state, gone);
}

/** Insert `file` before the file with id `beforeId`, or at the end if absent. */
function spliceFileBefore(
  files: V2File[],
  beforeId: string,
  file: V2File
): void {
  const target = files.findIndex((f) => f.id === beforeId);
  files.splice(target < 0 ? files.length : target, 0, file);
}

const isGcode = (file: V2File) => file.kind === 'gcode';

const columnKeys = (files: V2File[], controller: string) =>
  files.filter((f) => isGcode(f) && f.controller === controller).map(cutKey);

// A column that doesn't pair file for file keeps the order its user gave it
function mirrorOrderFrom(files: V2File[], source: string): void {
  const order = columnKeys(files, source);
  const rank = (file: V2File) => order.indexOf(cutKey(file));
  const others = new Set(
    files
      .filter(isGcode)
      .map((f) => f.controller)
      .filter((c) => c !== source)
  );
  others.forEach((controller) => {
    if (!pairsOneForOne(columnKeys(files, controller), order)) return;
    sortInPlace(
      files,
      (f) => isGcode(f) && f.controller === controller,
      (a, b) => rank(a) - rank(b)
    );
  });
}

/** Index of the last file in `controller`'s run, or -1 if it has none. */
function lastIndexForController(files: V2File[], controller: string): number {
  let last = -1;
  files.forEach((f, i) => {
    if (f.controller === controller) last = i;
  });
  return last;
}

const initialState: V2UploadState = {
  step: 0,
  maxStep: 0,
  basics: {
    title: '',
    description: '',
    category: '',
    material: 'Wood (hard)',
    difficulty: '',
    license: 'CC BY-SA',
    tags: [],
  },
  files: [],
  videoLinks: [],
  thumbnailFileId: null,
  uploadingFileIds: [],
  uploadProgress: 0,
  uploadTotal: 0,
  parsing: false,
  parseProgress: 0,
  parsedReady: false,
  orderConfirmed: false,
  extractedData: null,
  camParsing: false,
  camExtracted: null,
  camExtractedFileId: null,
  jobDetails: EMPTY_JOB_DETAILS,
  focusField: null,
  operations: [],
  submitted: false,
  submitting: false,
  postId: null,
  mode: 'single',
  groupId: null,
  multiStage: 'workpiece',
  workpieceIndex: 1,
  childIds: [],
  editingChildId: null,
  groupSubmitCounts: null,
  groupDetails: {
    title: '',
    description: '',
    category: '',
    tags: [],
    difficulty: '',
    license: 'CC BY-SA',
  },
  groupCover: null,
};

/** Everything a new workpiece must NOT inherit is reset by rebuilding from
 * `initialState`; anything group-scoped has to be listed here or adding a new
 * group field silently drops it on the second workpiece. */
const GROUP_SCOPED = [
  'mode',
  'groupId',
  'childIds',
  'groupDetails',
  'groupCover',
] as const satisfies readonly (keyof V2UploadState)[];

const v2UploadSlice = createSlice({
  name: 'v2Upload',
  initialState,
  reducers: {
    setStep(state, action: PayloadAction<number>) {
      state.step = action.payload;
      // The furthest the user has ever been, which is what the rail locks on.
      // Tracking it here rather than in the rail is deliberate: every route to
      // a new step goes through this reducer — a step's Continue, the rail
      // itself, Sign-off's jump-to-edit, and useDraftResume replaying the
      // server's `currentPage` — so there is exactly one place it can be missed.
      state.maxStep = Math.max(state.maxStep, action.payload);
    },
    setFocusField(state, action: PayloadAction<string | null>) {
      state.focusField = action.payload;
    },
    setBasics(state, action: PayloadAction<Partial<V2Basics>>) {
      state.basics = { ...state.basics, ...action.payload };
    },
    addTag(state, action: PayloadAction<string>) {
      if (!state.basics.tags.includes(action.payload)) {
        state.basics.tags.push(action.payload);
      }
    },
    removeTag(state, action: PayloadAction<number>) {
      state.basics.tags.splice(action.payload, 1);
    },
    setLicense(state, action: PayloadAction<string>) {
      state.basics.license = action.payload;
    },
    addFiles(state, action: PayloadAction<V2File[]>) {
      state.files.push(...action.payload);
    },
    /** Drop every file of a kind, or all of them. The files are referenced from
     * two other places, so both have to let go: a tool group holding a removed
     * file would send a modelId the post no longer has, and a removed thumbnail
     * would leave the listing pointing at nothing. Parser output is cleared with
     * the last G-code file — it was read out of files that are now gone. */
    /** Drop one file by id. A targeted delete, not clearFiles' kind-wide sweep.
     * Removes the file from any operation holding it and clears the thumbnail if
     * it was the picked one. */
    removeFile(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.files = state.files.filter((f) => f.id !== id);
      state.operations.forEach((op) => {
        // eslint-disable-next-line no-param-reassign
        op.fileIds = op.fileIds.filter((fid) => fid !== id);
      });
      if (state.thumbnailFileId === id) state.thumbnailFileId = null;
      if (!state.files.some((f) => f.kind === 'gcode')) {
        state.extractedData = null;
        state.parsedReady = false;
      }
      clearCamIfProducerGone(state, new Set([id]));
    },
    /** Undo a removeFile/clearFiles whose server-side delete failed: merge the
     * file back where it was. Idempotent and targeted — it re-adds only what's
     * actually missing, so a file deleted concurrently (and successfully) isn't
     * resurrected as a side effect. */
    restoreFile(
      state,
      action: PayloadAction<{
        file: V2File;
        index: number;
        opId: string | null;
        opBeforeFileId: string | null;
        wasThumbnail: boolean;
        extractedData: GcodeExtractedData | null;
      }>
    ) {
      const { file, index, opId, opBeforeFileId, wasThumbnail, extractedData } =
        action.payload;
      reAddFile(state, file, index);
      reAddToOp(state, opId, opBeforeFileId, file.id);
      reAddThumbnail(state, wasThumbnail, file.id);
      reAddParse(state, file, extractedData);
    },
    clearFiles(state, action: PayloadAction<V2File['kind'] | 'all'>) {
      const kind = action.payload;
      const gone = new Set(
        state.files
          .filter((f) => kind === 'all' || f.kind === kind)
          .map((f) => f.id)
      );
      if (gone.size === 0) return;

      state.files = state.files.filter((f) => !gone.has(f.id));
      state.operations.forEach((op) => {
        // eslint-disable-next-line no-param-reassign
        op.fileIds = op.fileIds.filter((id) => !gone.has(id));
      });
      afterFilesRemoved(state, gone);
    },
    /** Ignores a link the post already carries: the backend stores one
     * attachment per URL, and the same video twice is the same video. */
    addVideoLink(state, action: PayloadAction<string>) {
      if (!state.videoLinks.includes(action.payload)) {
        state.videoLinks.push(action.payload);
      }
    },
    removeVideoLink(state, action: PayloadAction<string>) {
      state.videoLinks = state.videoLinks.filter((v) => v !== action.payload);
    },
    setVideoLinks(state, action: PayloadAction<string[]>) {
      state.videoLinks = action.payload;
    },
    setThumbnailFileId(state, action: PayloadAction<string | null>) {
      state.thumbnailFileId = action.payload;
    },
    renameFile(state, action: PayloadAction<{ fileId: string; name: string }>) {
      const file = state.files.find((f) => f.id === action.payload.fileId);
      if (file) file.name = action.payload.name;
    },
    setUploadingFileIds(state, action: PayloadAction<string[]>) {
      state.uploadingFileIds = action.payload;
    },
    /** A new total starts a new run, so the count starts with it. Leaving the
     * old progress standing is what read "13 of 1 file · 1300%" on a second
     * pass through the step: every earlier upload was still counted. */
    setUploadTotal(state, action: PayloadAction<number>) {
      state.uploadTotal = action.payload;
      state.uploadProgress = 0;
    },
    incrementUploadProgress(state) {
      // Never past the total: a duplicate hash can resolve twice, and the bar
      // must not claim more files than the run has.
      state.uploadProgress = Math.min(
        state.uploadProgress + 1,
        state.uploadTotal
      );
    },
    setFileToolpaths(
      state,
      action: PayloadAction<Record<string, string | null>>
    ) {
      state.files.forEach((file) => {
        if (file.id in action.payload) {
          file.toolpathD = action.payload[file.id];
        }
      });
    },
    // Per-file parser output for the detail modal — same shape as toolpaths,
    // one full record per file instead of one path.
    setFileExtracted(
      state,
      action: PayloadAction<Record<string, GcodeExtractedData | null>>
    ) {
      state.files.forEach((file) => {
        if (file.id in action.payload) {
          file.extracted = action.payload[file.id];
        }
      });
    },
    // Carries only ids (v2 file id -> attachment id), never Files, so the
    // store's serializableCheck ignore-list stays limited to v2Upload/addFiles.
    setFileAttachmentIds(state, action: PayloadAction<Record<string, string>>) {
      state.files.forEach((file) => {
        const attachmentId = action.payload[file.id];
        if (attachmentId) file.attachmentId = attachmentId;
      });
    },
    setParsing(state, action: PayloadAction<boolean>) {
      state.parsing = action.payload;
    },
    setParseProgress(state, action: PayloadAction<number>) {
      state.parseProgress = action.payload;
    },
    setParsedReady(state, action: PayloadAction<boolean>) {
      state.parsedReady = action.payload;
    },
    setExtractedData(state, action: PayloadAction<GcodeExtractedData | null>) {
      state.extractedData = action.payload;
    },
    setCamExtracted(state, action: PayloadAction<CamExtractedData | null>) {
      state.camExtracted = action.payload;
    },
    /** The id of the V2File that produced `camExtracted`, dispatched alongside
     * it from FilesStep. Kept as its own action (not folded into the payload
     * above) so `setCamExtracted(null)` — clearing it outright — doesn't also
     * have to carry a redundant null id. */
    setCamExtractedFileId(state, action: PayloadAction<string | null>) {
      state.camExtractedFileId = action.payload;
    },
    setCamParsing(state, action: PayloadAction<boolean>) {
      state.camParsing = action.payload;
    },
    setJobDetails(state, action: PayloadAction<Partial<V2JobDetails>>) {
      state.jobDetails = { ...state.jobDetails, ...action.payload };
    },
    /** Seed the whole Job details form from the g-code + CAM parsers, once.
     * Seeding per-field would re-flip a boolean the user set to No every time a
     * parse re-lands; a single `seeded` guard keeps every edit after the first
     * fill — including any CAM file dropped after this first fires. */
    seedJobDetailsFromParser(state, action: PayloadAction<GcodeExtractedData>) {
      if (state.jobDetails.seeded) return;
      state.jobDetails = mergeJobDetails(
        state.jobDetails,
        action.payload,
        state.camExtracted,
        state.files
      );
    },
    updateFileController(
      state,
      action: PayloadAction<{ fileId: string; controller: string }>
    ) {
      const file = state.files.find((f) => f.id === action.payload.fileId);
      if (file) {
        file.controller = action.payload.controller;
        file.badge = action.payload.controller.toUpperCase();
        // Named by the parser or placed by hand — either way it's no longer a
        // guess off the extension.
        file.unsure = false;
      }
    },
    /**
     * Moves a file to sit immediately before `beforeFileId`. With no target it
     * lands at the end of `controller`'s run (the columns are a filtered view of
     * this one list, so "end of the column" means after its last member), or at
     * the end of the list when no controller is given either.
     *
     * The insertion index is read AFTER the file is spliced out — computing it
     * first lands the file one slot late whenever it moves down the list.
     */
    reorderFile(
      state,
      action: PayloadAction<{
        fileId: string;
        beforeFileId?: string | null;
        controller?: string;
      }>
    ) {
      const { fileId, beforeFileId, controller } = action.payload;
      const idx = state.files.findIndex((f) => f.id === fileId);
      if (idx < 0) return;
      const [file] = state.files.splice(idx, 1);

      if (beforeFileId) {
        spliceFileBefore(state.files, beforeFileId, file);
      } else if (controller) {
        const last = lastIndexForController(state.files, controller);
        state.files.splice(last + 1, 0, file);
      } else {
        state.files.push(file);
      }

      if (isGcode(file)) mirrorOrderFrom(state.files, file.controller);
      state.orderConfirmed = false;
    },
    /** Parser-derived carve order, applied across every controller column. */
    sortGcodeByCutOrder(state) {
      sortInPlace(state.files, isGcode, byCutOrder);
    },
    setOrderConfirmed(state, action: PayloadAction<boolean>) {
      state.orderConfirmed = action.payload;
    },
    setOperations(state, action: PayloadAction<V2Operation[]>) {
      state.operations = action.payload;
    },
    renameOperation(
      state,
      action: PayloadAction<{ opId: string; name: string }>
    ) {
      const op = state.operations.find((o) => o.id === action.payload.opId);
      if (op) op.name = action.payload.name;
    },
    /** Replaces an operation's bits wholesale — what the picker dialog saves,
     * and what mirroring a row onto the other controllers writes. */
    setOperationBits(
      state,
      action: PayloadAction<{ opId: string; bits: V2Bit[] }>
    ) {
      const op = state.operations.find((o) => o.id === action.payload.opId);
      if (op) op.bits = action.payload.bits;
    },
    setSubmitted(state, action: PayloadAction<boolean>) {
      state.submitted = action.payload;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.submitting = action.payload;
    },
    setPostId(state, action: PayloadAction<string | null>) {
      state.postId = action.payload;
    },
    setMode(state, action: PayloadAction<V2UploadState['mode']>) {
      state.mode = action.payload;
    },
    setGroupId(state, action: PayloadAction<string | null>) {
      state.groupId = action.payload;
    },
    setMultiStage(state, action: PayloadAction<V2UploadState['multiStage']>) {
      state.multiStage = action.payload;
    },
    setChildIds(state, action: PayloadAction<string[]>) {
      state.childIds = action.payload;
    },
    setWorkpieceIndex(state, action: PayloadAction<number>) {
      state.workpieceIndex = action.payload;
    },
    addChildId(state, action: PayloadAction<string>) {
      if (!state.childIds.includes(action.payload)) {
        state.childIds.push(action.payload);
      }
    },
    removeChildId(state, action: PayloadAction<string>) {
      state.childIds = state.childIds.filter((id) => id !== action.payload);
    },
    setGroupDetails(state, action: PayloadAction<Partial<V2GroupDetails>>) {
      state.groupDetails = { ...state.groupDetails, ...action.payload };
    },
    addGroupTag(state, action: PayloadAction<string>) {
      if (!state.groupDetails.tags.includes(action.payload)) {
        state.groupDetails.tags.push(action.payload);
      }
    },
    removeGroupTag(state, action: PayloadAction<number>) {
      state.groupDetails.tags.splice(action.payload, 1);
    },
    /** Reopen an already-authored workpiece from the review screen. */
    openWorkpiece(
      state,
      action: PayloadAction<{ postId: string; index: number }>
    ) {
      const kept = Object.fromEntries(
        GROUP_SCOPED.map((key) => [key, state[key]])
      );
      return {
        ...initialState,
        ...kept,
        multiStage: 'workpiece' as const,
        workpieceIndex: action.payload.index,
        postId: action.payload.postId,
        editingChildId: action.payload.postId,
      };
    },
    clearEditingChild(state) {
      state.editingChildId = null;
    },
    setGroupSubmitCounts(
      state,
      action: PayloadAction<V2UploadState['groupSubmitCounts']>
    ) {
      state.groupSubmitCounts = action.payload;
    },
    startNextWorkpiece(state) {
      const kept = Object.fromEntries(
        GROUP_SCOPED.map((key) => [key, state[key]])
      );
      return {
        ...initialState,
        ...kept,
        multiStage: 'workpiece' as const,
        workpieceIndex: state.workpieceIndex + 1,
      };
    },
    setGroupCover(state, action: PayloadAction<V2UploadState['groupCover']>) {
      const previous = state.groupCover?.preview;
      // A restored cover's preview is a CDN URL, not ours to revoke.
      if (
        previous?.startsWith('blob:') &&
        previous !== action.payload?.preview
      ) {
        URL.revokeObjectURL(previous);
      }
      state.groupCover = action.payload;
    },
    resetAll() {
      return initialState;
    },
  },
});

export const {
  setStep,
  setFocusField,
  setBasics,
  addTag,
  removeTag,
  setLicense,
  addFiles,
  clearFiles,
  removeFile,
  restoreFile,
  addVideoLink,
  removeVideoLink,
  setVideoLinks,
  setThumbnailFileId,
  renameFile,
  setUploadingFileIds,
  setUploadTotal,
  incrementUploadProgress,
  setFileToolpaths,
  setFileExtracted,
  setFileAttachmentIds,
  setParsing,
  setParseProgress,
  setParsedReady,
  setExtractedData,
  setCamExtracted,
  setCamExtractedFileId,
  setCamParsing,
  setJobDetails,
  seedJobDetailsFromParser,
  updateFileController,
  reorderFile,
  sortGcodeByCutOrder,
  setOrderConfirmed,
  setOperations,
  renameOperation,
  setOperationBits,
  setSubmitted,
  setSubmitting,
  setPostId,
  setMode,
  setGroupId,
  setMultiStage,
  setChildIds,
  setWorkpieceIndex,
  addChildId,
  removeChildId,
  openWorkpiece,
  clearEditingChild,
  setGroupSubmitCounts,
  setGroupDetails,
  addGroupTag,
  removeGroupTag,
  startNextWorkpiece,
  setGroupCover,
  resetAll,
} = v2UploadSlice.actions;

export const selectV2 = (state: { v2Upload: V2UploadState }) => state.v2Upload;

export default v2UploadSlice.reducer;
